/**
 * PolishButton
 * --------------------------------------------------------------------------
 * One-click "Polish my description" button.
 *
 * Sends the current draft to GPT-5.2, which returns a version rewritten in
 * formal ICAO/GACAR aviation-safety register — without inventing new facts.
 *
 * Props:
 *   - text (string)               current draft
 *   - kind ('incident'|'wildlife'|'fatigue'|'risk')  register flavour
 *   - lang ('en' | 'ar')          output language
 *   - onApply(polished)           receives the polished string
 *   - token                       auth JWT
 *   - minChars (default 30)       don't offer below this length
 */
import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PolishButton({ text = '', kind = 'incident', lang = 'en', onApply, token, minChars = 30 }) {
  const [loading, setLoading] = useState(false);
  const trimmed = (text || '').trim();
  const disabled = loading || trimmed.length < minChars;

  const run = async () => {
    if (disabled) return;
    // Capture the original text BEFORE we polish so undo/toast can restore it.
    const originalText = trimmed;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/voice-incident/polish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: trimmed, kind, lang }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      if (j.polished) {
        onApply(j.polished);
        // Industry practice: show an "Undo" affordance for ~30s so the user
        // can revert if the AI rewrite changed factual context. Restoration
        // is purely client-side — the polished text was never committed to
        // the DB by this single-item flow.
        toast.success('Description polished — review carefully.', {
          description: 'AI rewrites may lose subtle context. Re-read before saving.',
          duration: 30000,
          action: {
            label: 'Undo',
            onClick: () => {
              onApply(originalText);
              toast.info('Original text restored.');
            },
          },
        });
      } else {
        toast.warning('Polish returned no content.');
      }
    } catch (e) {
      toast.error(`Polish failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={run}
      disabled={disabled}
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-[11px] text-violet-300 hover:text-violet-100 hover:bg-violet-500/10 disabled:opacity-40"
      data-testid={`polish-btn-${kind}`}
      title={trimmed.length < minChars
        ? `Type at least ${minChars} characters first`
        : 'Rewrite in formal aviation-safety register (GPT-5.2)'}
    >
      {loading ? (
        <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Polishing…</>
      ) : (
        <><Sparkles className="w-3 h-3 mr-1.5" /> Polish my description</>
      )}
    </Button>
  );
}
